import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { extractBrandPersonaFromFile } from '../../../API/agent';
import { OFFICE_UPLOAD_ACCEPT, OFFICE_UPLOAD_ALLOWED_EXTENSIONS, SUPPORTED_UPLOAD_FORMATS_LABEL } from '../uploadFileConfig';

const useStyles = makeStyles(() => ({
  mainContainer: {
    width: '100%',
    maxWidth: '700px',
    margin: '0 auto 30px auto',
  },
  actions: {
    marginTop: '16px',
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  helper: {
    marginTop: '10px',
  },
}));

const MAX_SIZE_BYTES = 15 * 1024 * 1024;

const BrandBriefUpload = props => {
  const classes = useStyles();
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const extractionStatus = props.surveyAnswersState?.Brand_Extraction_Status;
  const requestIdRef = useRef(0);
  const currentAnalysisModeRef = useRef(props.surveyAnswersState?.analysis_mode);

  useEffect(() => {
    currentAnalysisModeRef.current = props.surveyAnswersState?.analysis_mode;
  }, [props.surveyAnswersState?.analysis_mode]);

  const extracted = useMemo(() => extractionStatus === 'completed', [extractionStatus]);

  const validateFile = file => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!OFFICE_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)) return 'Only PDF, DOC, DOCX, PPT, and PPTX files are supported.';
    if (file.size > MAX_SIZE_BYTES) return 'File is too large. Maximum allowed size is 15 MB.';
    return '';
  };

  const onUpload = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError('');
    props.handleSurveyAnswers('processing', 'Brand_Extraction_Status');
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      const result = await extractBrandPersonaFromFile({
        file,
        surveyId: props.surveyAnswersState?.id,
        context: props.surveyAnswersState,
      });

      const isLatestRequest = requestIdRef.current === requestId;
      const analysisModeStillUpload = currentAnalysisModeRef.current === 'upload_brand_document';
      if (!isLatestRequest || !analysisModeStillUpload) {
        return;
      }

      if (result?.err) {
        throw new Error(result?.errMessage || 'Extraction failed.');
      }

      const openEnded = result?.result?.openEnded || {};
      props.handleSurveyAnswers(openEnded?.q1_brand_as_person || '', 'Brand_OpenEnded_Q1');
      props.handleSurveyAnswers(openEnded?.q2_unique_offer || '', 'Brand_OpenEnded_Q2');
      props.handleSurveyAnswers(openEnded?.q3_ideal_customer || '', 'Brand_OpenEnded_Q3');
      props.handleSurveyAnswers(openEnded?.q4_customer_relationship || '', 'Brand_OpenEnded_Q4');
      props.handleSurveyAnswers(openEnded?.q5_cultural_spaces || '', 'Brand_OpenEnded_Q5');
      props.handleSurveyAnswers(openEnded?.q6_social_values || '', 'Brand_OpenEnded_Q6');
      props.handleSurveyAnswers(result?.result?.uploadedFileName || file.name, 'Brand_Uploaded_File_Name');
      props.handleSurveyAnswers(result?.result?.uploadedFileUrl || '', 'Brand_Uploaded_File_Url');
      props.handleSurveyAnswers('completed', 'Brand_Extraction_Status');
      props.handleSurveyAnswers('pending', 'Brand_Classification_Status');
    } catch (err) {
      if (requestIdRef.current === requestId && currentAnalysisModeRef.current === 'upload_brand_document') {
        setError(err.message || 'Extraction failed.');
        props.handleSurveyAnswers('failed', 'Brand_Extraction_Status');
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setUploading(false);
      }
      event.target.value = '';
    }
  };

  return (
    <Box className={classes.mainContainer}>
      <Typography variant="h3">Upload brand document</Typography>
      <Typography variant="body1" className={classes.helper}>
        Upload a document that describes the brand. Ideally include brand guidelines or documents containing mission, vision,
        history, objectives, target audience, positioning, tone, values, product benefits, and similar information. The more
        comprehensive the document, the better the analysis.
      </Typography>
      <Typography variant="body1" className={classes.helper}>
        {SUPPORTED_UPLOAD_FORMATS_LABEL}
      </Typography>

      <Typography variant="body1" className={classes.helper}>
        Only one document can be uploaded. If you have multiple files, please merge them into one PDF and upload that single file.
      </Typography>
      <Box className={classes.actions}>
        <Button variant="contained" component="label" disabled={uploading} style={{ background: '#19989B', color: '#fff' }}>
          {uploading ? (
            <>
              <CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} /> Analyzing...
            </>
          ) : (
            'Choose file'
          )}
          <input
            hidden
            type="file"
            accept={OFFICE_UPLOAD_ACCEPT}
            onChange={onUpload}
          />
        </Button>
      </Box>
      {extracted ? <Typography style={{ marginTop: 12 }}>Analysis completed. You can edit all generated answers in the next steps.</Typography> : null}
      {error ? <Typography style={{ marginTop: 12, color: '#D32F2F' }}>{error} Please retry with another file.</Typography> : null}
    </Box>
  );
};

export default BrandBriefUpload;
